import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { hrRequisitionApi } from "../api";
import { isExternalWorkflow, isGoodsRequisition, isAssetRequisition } from "../types";
import type { HrRequisition } from "../types";
import { ReasonModal } from "#/components/common/ReasonModal";
import { useAuth } from "#/context/AuthProvider";
import { StockIssuePanel, AssetFulfillPanel } from "./RequisitionFulfillmentPage";

function useAsyncData<T>(fn: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rev, setRev] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fn()
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(String(e?.message ?? e)); setLoading(false); } });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, rev]);

  return { data, loading, error, reload: () => setRev((r) => r + 1) };
}

const STATUS_CLASSES: Record<string, string> = {
  DRAFT:                "bg-slate-100 text-slate-600",
  SUBMITTED:            "bg-amber-50 text-amber-700 border border-amber-200",
  PENDING_APPROVAL:     "bg-amber-50 text-amber-700 border border-amber-200",
  APPROVED:             "bg-emerald-100 text-emerald-800",
  FULFILLED:            "bg-blue-100 text-blue-700",
  PARTIAL:              "bg-sky-100 text-sky-700",
  PURCHASED:            "bg-blue-100 text-blue-700",
  CLOSED:               "bg-slate-200 text-slate-600",
  REJECTED:             "bg-red-100 text-red-700",
  CANCELLED:            "bg-red-50 text-red-500",
  QUOTATION_REQUESTED:  "bg-purple-100 text-purple-700",
  QUOTATIONS_RECEIVED:  "bg-indigo-100 text-indigo-700",
  BUDGET_APPROVED:      "bg-teal-100 text-teal-700",
  BUDGET_REJECTED:      "bg-red-100 text-red-700",
  EVALUATED:            "bg-orange-100 text-orange-700",
};

function StatusBadge({ value }: { value?: string | null }) {
  const cls = STATUS_CLASSES[value ?? ""] ?? "bg-slate-100 text-slate-600";
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}>
      {(value ?? "").replace(/_/g, " ")}
    </span>
  );
}

function fmt(date?: string | null) {
  if (!date) return null;
  return new Date(date).toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" });
}

/** External procurement workflow stage tracker */
function ProcurementTimeline({ req }: { req: HrRequisition }) {
  const stages: { label: string; statusKey: string; date?: string | null; notes?: string | null }[] = [
    { label: "Submitted",          statusKey: "SUBMITTED",            date: req.submittedAt },
    { label: "Quotation Requested",statusKey: "QUOTATION_REQUESTED",  date: req.quotationRequestedAt, notes: req.procurementNotes },
    { label: "Quotations Received",statusKey: "QUOTATIONS_RECEIVED",  date: req.quotationsReceivedAt },
    { label: "Budget Decision",    statusKey: "BUDGET_APPROVED",      date: req.budgetDecisionAt,     notes: req.budgetDecisionNotes },
    { label: "Evaluation",         statusKey: "EVALUATED",            date: req.evaluatedAt,          notes: req.evaluationNotes },
    { label: "MD Approved",        statusKey: "APPROVED",             date: req.approvedAt },
  ];

  const statusOrder = [
    "DRAFT","SUBMITTED","PENDING_APPROVAL",
    "QUOTATION_REQUESTED","QUOTATIONS_RECEIVED",
    "BUDGET_APPROVED","BUDGET_REJECTED","EVALUATED",
    "APPROVED","FULFILLED","PARTIAL",
  ];

  const currentIdx = statusOrder.indexOf(req.status);
  const isBudgetRejected = req.status === "BUDGET_REJECTED";

  return (
    <div className="rounded-2xl border bg-white p-5 shadow-sm">
      <h2 className="mb-4 border-b pb-2 font-bold text-slate-900">Procurement Workflow Progress</h2>

      {isBudgetRejected && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Budget was rejected. This requisition requires revision before it can proceed.
          {req.budgetDecisionNotes && <span className="block mt-1 text-xs">{req.budgetDecisionNotes}</span>}
        </div>
      )}

      <div className="space-y-0">
        {stages.map((s, i) => {
          const stageIdx = statusOrder.indexOf(s.statusKey);
          const done    = stageIdx < currentIdx && currentIdx >= 1;
          const current = s.statusKey === req.status;
          const pending = !done && !current;

          return (
            <div key={s.statusKey} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold border-2 ${
                  current ? "bg-emerald-700 border-emerald-700 text-white" :
                  done    ? "bg-emerald-100 border-emerald-400 text-emerald-700" :
                            "bg-white border-slate-200 text-slate-400"
                }`}>
                  {done ? "✓" : i + 1}
                </div>
                {i < stages.length - 1 && (
                  <div className={`my-0.5 w-0.5 flex-1 min-h-[20px] ${done ? "bg-emerald-300" : "bg-slate-200"}`} />
                )}
              </div>

              <div className={`pb-4 flex-1 ${i === stages.length - 1 ? "pb-0" : ""}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`text-sm font-semibold ${current ? "text-emerald-800" : done ? "text-slate-700" : "text-slate-400"}`}>
                    {s.label}
                  </p>
                  {current && <StatusBadge value={req.status} />}
                  {s.date && (
                    <span className="text-xs text-slate-400">{fmt(s.date)}</span>
                  )}
                </div>
                {s.notes && !pending && (
                  <p className="mt-1 text-xs text-slate-500 leading-relaxed">{s.notes}</p>
                )}
                {pending && (
                  <p className="mt-0.5 text-xs text-slate-400 italic">Pending…</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function RequisitionViewPage() {
  const { id } = useParams<{ id: string }>();
  const [acting, setActing] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const { hasRole, hasAnyRole, hasPermission } = useAuth();
  const isAdmin = hasRole("SUPER_ADMIN") || hasAnyRole(["ADMIN"]);
  const canFulfillStock = isAdmin || hasPermission("HR_REQUISITION_FULFILL_STOCK");
  const canAssignAssets = isAdmin || hasPermission("ASSET_ASSIGN");

  const { data: req, loading, error, reload } = useAsyncData(
    () => hrRequisitionApi.get(Number(id)),
    [id],
  );

  async function handleSubmit() {
    if (!req) return;
    setActing(true);
    try {
      await hrRequisitionApi.submit(req.id);
      reload();
    } finally {
      setActing(false);
    }
  }

  async function handleCancel(reason: string) {
    if (!req) return;
    setActing(true);
    try {
      await hrRequisitionApi.cancel(req.id, reason || undefined);
      setShowCancelModal(false);
      reload();
    } finally {
      setActing(false);
    }
  }

  if (loading) return <div className="rounded-xl border bg-white p-5 text-sm text-slate-500">Loading…</div>;
  if (error) return (
    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      <strong>Error:</strong> {error}
    </div>
  );
  if (!req) return null;

  const isDraft = req.status === "DRAFT";
  const isSubmittedOrPending = req.status === "SUBMITTED" || req.status === "PENDING_APPROVAL";
  const reqType = req.requisitionType ?? req.type;
  const external = isExternalWorkflow(reqType);

  // A requisition with one line fulfilled but others still pending flips to PARTIAL, which the
  // pending-fulfillment queue doesn't list (server-side query scopes to APPROVED only) — so this
  // is offered here too, reachable directly by id (e.g. via Reports' Partial filter), regardless
  // of whether it still shows up in the queue.
  const canContinueFulfillment = req.status === "APPROVED" || req.status === "PARTIAL";
  const showStockPanel = canContinueFulfillment && isGoodsRequisition(reqType) && canFulfillStock;
  const showAssetPanel = canContinueFulfillment && isAssetRequisition(reqType) && canAssignAssets;

  return (
    <div className="space-y-5">
      {/* Nav */}
      <div className="flex items-center justify-between gap-3">
        <Link to="/hr/requisitions/mine" className="text-sm text-emerald-700 hover:underline">
          &larr; My Requisitions
        </Link>
        <div className="flex gap-2">
          {isDraft && (
            <>
              <Link
                to={`/hr/requisitions/${req.id}/edit`}
                className="rounded-xl border px-4 py-2 text-sm font-semibold text-blue-600 border-blue-300 hover:bg-blue-50"
              >
                Edit
              </Link>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={acting}
                className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-50"
              >
                Submit for Approval
              </button>
            </>
          )}
          {(isDraft || isSubmittedOrPending) && (
            <button
              type="button"
              onClick={() => setShowCancelModal(true)}
              disabled={acting}
              className="rounded-xl border border-red-300 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <ReasonModal
        open={showCancelModal}
        title="Cancel Requisition"
        label="Reason for cancellation"
        placeholder="Optional — let approvers know why this is being cancelled"
        confirmLabel="Cancel Requisition"
        confirmTone="danger"
        submitting={acting}
        onCancel={() => setShowCancelModal(false)}
        onConfirm={handleCancel}
      />

      {/* Escalation notice */}
      {(req.escalatedToPrNo ?? req.escalatedToPrSerialNo) && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
          This requisition was escalated to procurement as{" "}
          <strong>{req.escalatedToPrNo ?? req.escalatedToPrSerialNo}</strong>.
        </div>
      )}

      {/* Header card */}
      <div className="rounded-2xl border-2 border-emerald-700 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">Staff Requisition</p>
            <h1 className="mt-0.5 text-xl font-bold text-slate-900">
              {req.serialNo || `Requisition #${req.id}`}
            </h1>
            {reqType && (
              <p className="mt-0.5 text-xs text-slate-500 font-medium">
                {reqType.replace(/_/g, " ")}
                {external && <span className="ml-2 text-purple-600">· External Procurement</span>}
              </p>
            )}
          </div>
          <StatusBadge value={req.status} />
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
          {req.requesterName && (
            <div>
              <p className="text-slate-400 font-medium">Requester</p>
              <p className="text-slate-700 font-semibold">{req.requesterName}</p>
            </div>
          )}
          {req.departmentName && (
            <div>
              <p className="text-slate-400 font-medium">Department</p>
              <p className="text-slate-700 font-semibold">{req.departmentName}</p>
            </div>
          )}
          {req.submittedAt && (
            <div>
              <p className="text-slate-400 font-medium">Submitted</p>
              <p className="text-slate-700 font-semibold">{fmt(req.submittedAt)}</p>
            </div>
          )}
        </div>

        {req.purpose && (
          <p className="mt-3 text-sm text-slate-600 border-t pt-3 italic">{req.purpose}</p>
        )}
        {req.comments && (
          <p className="mt-2 text-sm text-slate-600">{req.comments}</p>
        )}
      </div>

      {/* Procurement workflow timeline — external types only */}
      {external && <ProcurementTimeline req={req} />}

      {/* Lines */}
      <div className="rounded-2xl border bg-white p-5 shadow-sm">
        <h2 className="mb-4 border-b pb-2 font-bold text-slate-900">Requisition Lines</h2>
        {(() => {
          const reqLines = req.lines ?? [];
          if (reqLines.length === 0) {
            return <p className="text-sm text-slate-400">No lines added yet.</p>;
          }
          const hasUnit = reqLines.some(l => l.unitOfMeasure);
          return (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-2">#</th>
                  <th className="px-4 py-2">Description</th>
                  <th className="px-4 py-2 text-right">Qty</th>
                  {hasUnit && (
                    <th className="px-4 py-2">Unit</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {reqLines.map((l) => (
                  <tr key={l.id ?? l.lineOrder}>
                    <td className="px-4 py-2 text-slate-500">{l.lineOrder}</td>
                    <td className="px-4 py-2 text-slate-800">{l.description}</td>
                    <td className="px-4 py-2 text-right font-semibold text-slate-700">{l.quantity}</td>
                    {hasUnit && (
                      <td className="px-4 py-2 text-slate-500">{l.unitOfMeasure ?? "—"}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          );
        })()}
      </div>

      {/* Fulfillment — available directly here (not just from the pending-fulfillment queue) so
          a Store Officer/Asset Manager can finish remaining lines on a PARTIAL requisition even
          after it's dropped out of the queue list. */}
      {showStockPanel && (
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <h2 className="mb-1 border-b pb-2 font-bold text-slate-900">Fulfillment</h2>
          <StockIssuePanel req={req} onDone={reload} />
        </div>
      )}
      {showAssetPanel && (
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <h2 className="mb-1 border-b pb-2 font-bold text-slate-900">Fulfillment</h2>
          <AssetFulfillPanel req={req} onDone={reload} />
        </div>
      )}
    </div>
  );
}
